import { useEffect, useRef, useState } from "react"
import { ArrowLeft } from "lucide-react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent } from "./ui/card"
import { Input } from "./ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface FormDataS051 {
  ROW_ID?: string
  numero_solicitud: string
  fecha: string
  inicio_hora: string
  inicio_temp: string
  inicio_hr: string
  inicio_termo: string
  fin_hora: string
  fin_temp: string
  fin_hr: string
  fin_termo: string
  flexiones_programadas: string
  tipo_agua: string
  tiempo_inmersion: string
  seco_100_con: string; seco_100_sin: string
  seco_1000_con: string; seco_1000_sin: string
  seco_10000_con: string; seco_10000_sin: string
  seco_20000_con: string; seco_20000_sin: string
  seco_48000_con: string; seco_48000_sin: string
  hum_100_con: string; hum_100_sin: string
  hum_1000_con: string; hum_1000_sin: string
  hum_10000_con: string; hum_10000_sin: string
  hum_18000_con: string; hum_18000_sin: string
  flex_prom_seco: string; flex_prom_hum: string
  dano_seco: string; dano_hum: string
  observaciones: string
  mostrarObservaciones: boolean
  desv_metodo: string
  realizo: string
  autorizo: string
}

interface Props {
  mode: "create" | "view" | "edit"
  initialData?: any
  onSave?: (data: any) => void
  onCancel?: () => void
  realizoUser?: string
  onRefreshBackend?: () => void
  rol?: string
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

const DEFAULTS: FormDataS051 = {
  numero_solicitud: '', fecha: '',
  inicio_hora: '', inicio_temp: '', inicio_hr: '', inicio_termo: '',
  fin_hora: '', fin_temp: '', fin_hr: '', fin_termo: '',
  flexiones_programadas: '', tipo_agua: '', tiempo_inmersion: '',
  seco_100_con: '', seco_100_sin: '', seco_1000_con: '', seco_1000_sin: '',
  seco_10000_con: '', seco_10000_sin: '', seco_20000_con: '', seco_20000_sin: '',
  seco_48000_con: '', seco_48000_sin: '',
  hum_100_con: '', hum_100_sin: '', hum_1000_con: '', hum_1000_sin: '',
  hum_10000_con: '', hum_10000_sin: '', hum_18000_con: '', hum_18000_sin: '',
  flex_prom_seco: '', flex_prom_hum: '', dano_seco: '', dano_hum: '',
  observaciones: '', mostrarObservaciones: false, desv_metodo: '', realizo: '', autorizo: '',
}

const TD = "border border-gray-500 px-1 py-0.5 align-middle text-xs"
const TERMO_OPTIONS = ['E-59', 'E-57', 'E-56']
const SECO_CICLOS = [100, 1000, 10000, 20000, 48000]
const HUM_CICLOS  = [100, 1000, 10000, 18000]

export function NMXS051Form({ mode, initialData = {}, onSave, onCancel, realizoUser, onRefreshBackend, rol, pedidoRowId, onAutosave }: Props) {
  const globalRol = useUserRol()
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol
  const [formData, setFormData] = useState<FormDataS051>({
    ...DEFAULTS,
    realizo: mode === 'create' && realizoUser ? realizoUser : '',
    ...initialData,
    mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true,
  })
  const [editMode, setEditMode] = useState(false)
  const [showRechazoModal, setShowRechazoModal] = useState(false)
  const [rechazoLoading, setRechazoLoading] = useState(false)

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData({
      ...DEFAULTS,
      realizo: realizoUser || '',
      ...initialData,
      mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true,
    })
    setEditMode(false)
  }, [initialData])

  const aceptadoRaw = initialData?.ACEPTADO ?? initialData?.aceptado
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : ''
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado'
  const isReadOnly = mode === 'view' && !editMode
  const [autosaveRowId, setAutosaveRowId] = useState<string>(formData?.ROW_ID || initialData?.ROW_ID || '')
  const [autosaveNomId, setAutosaveNomId] = useState<string>(initialData?.nomId || '')
  const rowId = autosaveRowId
  const nomId = autosaveNomId

  const set = (field: keyof FormDataS051, value: any) =>
    setFormData(prev => ({ ...prev, [field]: value }))

  const formPayload = () => {
    const { ROW_ID, ...rest } = formData
    return rest
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-S-051-1989 Numeral 7.1.1 Resistencia a la flexión',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) onSave(formData)
    } finally {
      setGuardando(false)
    }
  }

  const handleGuardarEdicion = () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-S-051? Esta acción no se puede deshacer.')) return
    if (onSave) onSave({ ...formData, ROW_ID: rowId, _EDIT_MODE: true } as any)
  }

  const handleAccion = async () => {
    if (!window.confirm('Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-S-051?')) return
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, 'aceptado', undefined, undefined, nomId || undefined)
      toast.success('Prueba aceptada correctamente.')
      onRefreshBackend?.()
    } catch (e: any) { toast.error(e.message) }
  }

  const inputCls = (extra?: string) =>
    `h-6 text-xs px-1 ${isReadOnly ? 'bg-transparent border-0 shadow-none cursor-not-allowed' : ''} ${extra ?? ''}`

  const cellInput = (field: keyof FormDataS051) => (
    <Input className="h-5 text-xs px-1 border-0 shadow-none w-full"
      value={(formData as any)[field] || ''} readOnly={isReadOnly}
      onChange={e => set(field, e.target.value)} />
  )

  const termoSelect = (field: keyof FormDataS051) =>
    isReadOnly ? (
      <Input className="h-5 text-xs px-1 border-0 shadow-none w-full"
        value={(formData as any)[field] || ''} readOnly />
    ) : (
      <Select value={(formData as any)[field] || ''} onValueChange={v => set(field, v)}>
        <SelectTrigger className="h-5 text-xs border-0 shadow-none"><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          {TERMO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    )

  return (
    <>
      {onCancel && (
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
      )}
      <Card className="w-full max-w-5xl mx-auto">
      <CardContent className="p-4 space-y-3">

        {/* ── MAIN TABLE ── */}
        <table className="w-full text-xs border-collapse border-2 border-gray-700">
          <tbody>
            {/* Row 1: NUMERO DE SOLICITUD + FECHA */}
            <tr>
              <td colSpan={3} className={TD}>
                <div className="flex items-center gap-2">
                  <span className="font-bold whitespace-nowrap">NUMERO DE SOLICITUD:</span>
                  <Input className={inputCls('flex-1')}
                    value={formData.numero_solicitud} readOnly={isReadOnly}
                    onChange={e => set('numero_solicitud', e.target.value)} />
                </div>
              </td>
              <td className={TD}>
                <div className="flex items-center gap-2">
                  <span className="font-bold whitespace-nowrap">FECHA:</span>
                  <Input type="date" className={inputCls('flex-1')}
                    value={formData.fecha} readOnly={isReadOnly}
                    onChange={e => set('fecha', e.target.value)} />
                </div>
              </td>
            </tr>

            {/* Row 2: Title */}
            <tr>
              <td colSpan={4} className={`${TD} text-center font-bold py-1`}>
                NMX-S-051-1989 Numeral 7.1.1 Resistencia a la flexión
              </td>
            </tr>

            {/* Row 3: Main content + Realizó/Autorizó */}
            <tr>
              <td colSpan={3} className="border border-gray-500 p-2 align-top">

                {/* DATOS DEL ESPECIMEN */}
                <table className="w-full text-xs border-collapse border border-gray-400 mb-2">
                  <tbody>
                    <tr>
                      <td colSpan={4} className="border border-gray-400 px-1 py-0.5 text-center font-bold">
                        DATOS DEL ESPECIMEN
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={4} className="border border-gray-400 px-1 py-0.5">
                        Tiempo de acondicionamiento: 48 h
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="border border-gray-400 px-1 py-0.5 text-center italic">
                        Inicio del acondicionamiento
                      </td>
                      <td colSpan={2} className="border border-gray-400 px-1 py-0.5 text-center italic">
                        Fin del acondicionamiento
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5 w-[25%]">Hora</td>
                      <td className="border border-gray-400 p-0.5 w-[25%]">{cellInput('inicio_hora')}</td>
                      <td className="border border-gray-400 px-1 py-0.5 w-[25%]">Hora</td>
                      <td className="border border-gray-400 p-0.5 w-[25%]">{cellInput('fin_hora')}</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5">Temperatura °C</td>
                      <td className="border border-gray-400 p-0.5">{cellInput('inicio_temp')}</td>
                      <td className="border border-gray-400 px-1 py-0.5">Temperatura °C</td>
                      <td className="border border-gray-400 p-0.5">{cellInput('fin_temp')}</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5">Humedad Relativa<br />%HR</td>
                      <td className="border border-gray-400 p-0.5">{cellInput('inicio_hr')}</td>
                      <td className="border border-gray-400 px-1 py-0.5">Humedad Relativa<br />%HR</td>
                      <td className="border border-gray-400 p-0.5">{cellInput('fin_hr')}</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5">ID Termohigrómetro</td>
                      <td className="border border-gray-400 p-0.5">{termoSelect('inicio_termo')}</td>
                      <td className="border border-gray-400 px-1 py-0.5">ID Termohigrómetro</td>
                      <td className="border border-gray-400 p-0.5">{termoSelect('fin_termo')}</td>
                    </tr>
                  </tbody>
                </table>

                {/* CONDICIONES DE ENSAYO */}
                <table className="w-full text-xs border-collapse border border-gray-400 mb-2">
                  <tbody>
                    <tr>
                      <td colSpan={4} className="border border-gray-400 px-1 py-0.5 text-center font-bold">
                        CONDICIONES DE ENSAYO
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="border border-gray-400 px-1 py-0.5 w-[55%]">
                        Flexiones programadas en el flexómetro
                      </td>
                      <td colSpan={2} className="border border-gray-400 p-0.5 w-[45%]">{cellInput('flexiones_programadas')}</td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="border border-gray-400 px-1 py-0.5 text-center" rowSpan={2}>
                        Prueba en húmedo
                      </td>
                      <td className="border border-gray-400 px-1 py-0.5">Tipo de agua utilizada</td>
                      <td className="border border-gray-400 p-0.5">{cellInput('tipo_agua')}</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5">Tiempo de inmersión</td>
                      <td className="border border-gray-400 p-0.5">{cellInput('tiempo_inmersion')}</td>
                    </tr>
                  </tbody>
                </table>

                {/* EVALUACIÓN */}
                <table className="text-xs border-collapse border border-gray-400" style={{ width: '90%' }}>
                  <tbody>
                    <tr>
                      <td colSpan={6} className="border border-gray-400 px-1 py-0.5 text-center font-bold">
                        EVALUACIÓN
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3} className="border border-gray-400 px-1 py-0.5 text-center italic">
                        Seco (acondicionado), 2 muestras
                      </td>
                      <td colSpan={3} className="border border-gray-400 px-1 py-0.5 text-center italic">
                        Húmedo, 2 muestras
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5" rowSpan={2}>Ciclos</td>
                      <td colSpan={2} className="border border-gray-400 px-1 py-0.5 text-center">Evaluación</td>
                      <td className="border border-gray-400 px-1 py-0.5" rowSpan={2}>Ciclos</td>
                      <td colSpan={2} className="border border-gray-400 px-1 py-0.5 text-center">Evaluación</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5 text-center">Con daño</td>
                      <td className="border border-gray-400 px-1 py-0.5 text-center">Tipo de daño</td>
                      <td className="border border-gray-400 px-1 py-0.5 text-center">Con daño</td>
                      <td className="border border-gray-400 px-1 py-0.5 text-center">Tipo de daño</td>
                    </tr>
                    {SECO_CICLOS.map((c, i) => {
                      const hc = HUM_CICLOS[i]
                      return (
                        <tr key={c}>
                          <td className="border border-gray-400 px-1 py-0.5 text-center">{c.toLocaleString()}</td>
                          <td className="border border-gray-400 p-0.5">
                            <Input className="h-5 text-xs px-1 border-0 shadow-none w-full"
                              value={(formData as any)[`seco_${c}_con`] || ''} readOnly={isReadOnly}
                              onChange={e => set(`seco_${c}_con` as keyof FormDataS051, e.target.value)} />
                          </td>
                          <td className="border border-gray-400 p-0.5">
                            <Input className="h-5 text-xs px-1 border-0 shadow-none w-full"
                              value={(formData as any)[`seco_${c}_sin`] || ''} readOnly={isReadOnly}
                              onChange={e => set(`seco_${c}_sin` as keyof FormDataS051, e.target.value)} />
                          </td>
                          <td className="border border-gray-400 px-1 py-0.5 text-center">
                            {hc ? hc.toLocaleString() : ''}
                          </td>
                          <td className="border border-gray-400 p-0.5">
                            {hc ? (
                              <Input className="h-5 text-xs px-1 border-0 shadow-none w-full"
                                value={(formData as any)[`hum_${hc}_con`] || ''} readOnly={isReadOnly}
                                onChange={e => set(`hum_${hc}_con` as keyof FormDataS051, e.target.value)} />
                            ) : null}
                          </td>
                          <td className="border border-gray-400 p-0.5">
                            {hc ? (
                              <Input className="h-5 text-xs px-1 border-0 shadow-none w-full"
                                value={(formData as any)[`hum_${hc}_sin`] || ''} readOnly={isReadOnly}
                                onChange={e => set(`hum_${hc}_sin` as keyof FormDataS051, e.target.value)} />
                            ) : null}
                          </td>
                        </tr>
                      )
                    })}

                    {/* RESULTADOS */}
                    <tr>
                      <td colSpan={6} className="border border-gray-400 px-1 py-0.5 text-center font-bold">
                        RESULTADOS
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5">Flexiones promedio</td>
                      <td colSpan={2} className="border border-gray-400 p-0.5">
                        <Input className="h-5 text-xs px-1 border-0 shadow-none w-full"
                          value={formData.flex_prom_seco} readOnly={isReadOnly}
                          onChange={e => set('flex_prom_seco', e.target.value)} />
                      </td>
                      <td className="border border-gray-400 px-1 py-0.5">Flexiones promedio</td>
                      <td colSpan={2} className="border border-gray-400 p-0.5">
                        <Input className="h-5 text-xs px-1 border-0 shadow-none w-full"
                          value={formData.flex_prom_hum} readOnly={isReadOnly}
                          onChange={e => set('flex_prom_hum', e.target.value)} />
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5">Daño presentado</td>
                      <td colSpan={2} className="border border-gray-400 p-0.5">
                        <Input className="h-5 text-xs px-1 border-0 shadow-none w-full"
                          value={formData.dano_seco} readOnly={isReadOnly}
                          onChange={e => set('dano_seco', e.target.value)} />
                      </td>
                      <td className="border border-gray-400 px-1 py-0.5">Daño presentado</td>
                      <td colSpan={2} className="border border-gray-400 p-0.5">
                        <Input className="h-5 text-xs px-1 border-0 shadow-none w-full"
                          value={formData.dano_hum} readOnly={isReadOnly}
                          onChange={e => set('dano_hum', e.target.value)} />
                      </td>
                    </tr>
                  </tbody>
                </table>

              </td>

              {/* Realizó / Autorizó */}
              <td className="border border-gray-500 p-2 align-top w-[18%]">
                <div className="space-y-6">
                  <div>
                    <div className="text-xs font-medium mb-1">Realizó:</div>
                    <Input value={formData.realizo} readOnly
                      className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
                  </div>
                  <div>
                    <div className="text-xs font-medium mb-1">Autorizó:</div>
                    <Input value={formData.autorizo} readOnly
                      className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
                  </div>
                </div>
              </td>
            </tr>

            {/* Row 4: Observaciones */}
            <tr>
              <td colSpan={4} className="border border-gray-500 px-2 py-1">
                <div className="font-medium mb-1 flex items-center gap-2">
                  Observaciones:
                  <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                    Mostrar en Reporte Cliente
                    <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                      onCheckedChange={(checked) => set('mostrarObservaciones', checked)} />
                  </span>
                </div>
                <Textarea value={formData.observaciones} readOnly={isReadOnly} rows={2}
                  className={`text-xs ${isReadOnly ? 'border-0 shadow-none bg-transparent resize-none' : ''}`}
                  onChange={e => set('observaciones', e.target.value)} />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Desviación al método */}
        <div>
          <label className="text-xs font-medium block mb-1">Desviación al método</label>
          <Textarea value={formData.desv_metodo} readOnly={isReadOnly} rows={2}
            className={`text-xs ${isReadOnly ? 'bg-muted-foreground/10 cursor-not-allowed' : ''}`}
            onChange={e => set('desv_metodo', e.target.value)} />
        </div>

        <div className="flex gap-2 justify-end">
          {onCancel && <Button variant="outline" onClick={handleBackFlush}>Cancelar</Button>}
          {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
            <>
              <Button variant="default" onClick={handleAccion}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={() => setEditMode(true)}>Editar</Button>
            </>
          )}
          {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
          {mode === 'create' && (
            <Button onClick={handleSave} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar Formulario'}</Button>
          )}
        </div>

      </CardContent>

      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={async (razon: string) => {
          setRechazoLoading(true)
          try {
            // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
            // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
            if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
            while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
            await actualizarAceptado(rowId, 'rechazado', razon, undefined, nomId || undefined)
            toast.success('Prueba rechazada')
            setShowRechazoModal(false)
            onRefreshBackend?.()
          } catch (e: any) { toast.error(e.message) }
          finally { setRechazoLoading(false) }
        }}
      />
    </Card>
    </>
  )
}
